import { useEffect, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type Jurisdiction={jurisdictionCode:string;verifiedRuleCount:number};
type Pack={packId:string;packKey:string;packVersion:number;jurisdictionCode:string;schoolYear:number;contentHash:string;publishedAt:string;ruleCount:number;current:boolean};
type Workspace={jurisdictions:Jurisdiction[];packs:Pack[]};

function fmt(value:string){
  return new Date(value).toLocaleString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"});
}

export function AdminAssurancePolicyPackPanel({client}:{client:PlatformClient}){
  const [data,setData]=useState<Workspace|null>(null);
  const [jurisdiction,setJurisdiction]=useState("");
  const [schoolYear,setSchoolYear]=useState(String(new Date().getFullYear()));
  const [note,setNote]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    client.rpc("admin_assurance_policy_pack_workspace").then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setData(next);
      setJurisdiction((current)=>next.jurisdictions.some((item)=>item.jurisdictionCode===current)?current:next.jurisdictions[0]?.jurisdictionCode || "");
    });
    return()=>{active=false;};
  },[client,refresh]);

  async function publish(){
    setBusy(true);setError("");setMessage("");
    const {data,error}=await client.rpc("admin_publish_assurance_policy_pack",{
      p_jurisdiction_code:jurisdiction,
      p_school_year:Number(schoolYear),
      p_note:note.trim() || null
    });
    setBusy(false);
    if(error){setError(error.message);return;}
    const result=data as {packVersion:number;ruleCount:number;contentHash:string};
    setMessage("Policy Pack v"+result.packVersion+" 발행 완료 · Rule "+result.ruleCount+"개");
    setNote("");
    setRefresh((value)=>value+1);
  }

  if(!data)return <section className="governance-card"><p className="muted">Policy Pack 상태를 불러오는 중…</p></section>;

  return (
    <section className="erp09-pack-admin governance-card">
      <div className="panel-title">
        <div><strong>Assurance Policy Pack</strong><span>Verified Rule만 immutable snapshot으로 배포합니다.</span></div>
        <span className="status-pill">{data.packs.length} packs</span>
      </div>
      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="erp09-pack-publisher">
        <label>관할
          <select value={jurisdiction} onChange={(e)=>setJurisdiction(e.target.value)}>
            {data.jurisdictions.map((item)=><option key={item.jurisdictionCode} value={item.jurisdictionCode}>{item.jurisdictionCode} · Verified Rule {item.verifiedRuleCount}</option>)}
          </select>
        </label>
        <label>학년도<input inputMode="numeric" value={schoolYear} onChange={(e)=>setSchoolYear(e.target.value)} /></label>
        <label className="wide">발행 메모<input value={note} onChange={(e)=>setNote(e.target.value)} /></label>
        <button className="primary compact-button" disabled={busy || !jurisdiction || !schoolYear} onClick={publish}>{busy?"발행 중…":"새 Policy Pack 발행"}</button>
      </div>

      {!data.jurisdictions.length && (
        <div className="erp09-pack-empty">
          <strong>발행 가능한 Verified Rule이 없습니다.</strong>
          <p>Source와 Rule을 검증한 뒤 Policy Pack을 발행할 수 있습니다. Draft Rule은 Pack에 포함되지 않습니다.</p>
        </div>
      )}

      <div className="erp09-pack-list">
        {data.packs.slice(0,12).map((item)=>(
          <article key={item.packId}>
            <span><strong>{item.packKey} · v{item.packVersion}</strong><small>{item.jurisdictionCode} · {item.schoolYear} · Rule {item.ruleCount}</small></span>
            <span><b>{item.current?"현재":"이전"}</b><small>{fmt(item.publishedAt)}</small></span>
          </article>
        ))}
      </div>
    </section>
  );
}
